/**
 * IrrigationStatusStrip
 *
 * The headline answer at the top of a live irrigation page: is anything
 * watering, what is due next, and the conditions around it. Shared by the
 * all-farms overview and the single-farm page so the two can never answer the
 * same question in two different ways.
 *
 * "Watering" and "should be watering" are different claims. A bridge site runs
 * its timers on the laptop, and a valve can be inside its window with nothing
 * heard from the node since it opened — worth showing, but not worth counting
 * as confirmed water on the ground.
 */

import { useMemo, type ReactNode } from 'react';
import {
  CheckCircle2,
  CloudRain,
  Droplets,
  Thermometer,
  Wifi,
  Wind,
} from 'lucide-react';
import type { OverviewFarm } from '../../services/irrigationOverview';
import type { UpcomingRun, ZoneRun } from '../../utils/irrigationRuns';
import { formatRunTime } from '../../utils/irrigationRuns';
import { formatStartClock } from '../../utils/irrigationClock';
import { useOrgTimezone } from '../../utils/timezone';
import { formatNum, toNum, toNumOr } from '@/utils/numeric';
import '../../styles/IrrigationLive.css';

interface IrrigationStatusStripProps {
  /** Farms in scope — weather and tank levels are read from these. */
  scopeFarms: OverviewFarm[];
  /** Runs and upcoming runs, already scoped to the same farms. */
  runs: ZoneRun[];
  upcoming: UpcomingRun[];
  routersOnline: number;
  routersTotal: number;
  /** False when no site reported any schedule at all — timings are unavailable. */
  hasSchedule: boolean;
  secondsSinceUpdate: number | null;
  /** Farm filter chips, or anything else that belongs inside the strip. */
  children?: ReactNode;
}

export default function IrrigationStatusStrip({
  scopeFarms,
  runs,
  upcoming,
  routersOnline,
  routersTotal,
  hasSchedule,
  secondsSinceUpdate,
  children,
}: IrrigationStatusStripProps) {
  const orgTz = useOrgTimezone();

  const confirmedRuns = useMemo(() => runs.filter((r) => r.confirmed), [runs]);
  const dueRuns = useMemo(() => runs.filter((r) => !r.confirmed), [runs]);

  /** Weather and tank levels for the farms in scope — the context around a run. */
  const conditions = useMemo(() => {
    const wx = scopeFarms
      .flatMap((f) => f.weather_stations)
      .find((w) => w.latest_observation)?.latest_observation;
    const tanks = scopeFarms
      .flatMap((f) => f.sensors)
      .filter((s) => s.type_key === 'tank_level' && s.latest_value != null);
    const lowestTank = tanks.length
      ? Math.min(...tanks.map((s) => s.latest_value as number))
      : null;
    return { wx, lowestTank };
  }, [scopeFarms]);

  return (
    <div
      className={`rounded-2xl border p-5 ${
        confirmedRuns.length > 0
          ? 'border-blue-200 bg-blue-50'
          : dueRuns.length > 0
            ? 'border-amber-200 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {confirmedRuns.length > 0 ? (
            <span className="live-dot mt-0.5 h-3 w-3 shrink-0 rounded-full bg-blue-500" />
          ) : dueRuns.length > 0 ? (
            <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-amber-500" />
          ) : (
            <CheckCircle2 className="h-6 w-6 shrink-0 text-green-500" />
          )}
          <div>
            <p className="text-xl font-bold text-gray-900">
              {confirmedRuns.length > 0
                ? `${confirmedRuns.length} zone${confirmedRuns.length === 1 ? '' : 's'} watering now`
                : dueRuns.length > 0
                  ? `${dueRuns.length} zone${dueRuns.length === 1 ? '' : 's'} should be watering now`
                  : 'Nothing watering right now'}
            </p>
            <p className="text-sm text-gray-600">
              {confirmedRuns.length === 0 && dueRuns.length > 0 ? (
                'The site schedule has it running; nothing has been heard from the node since it was due.'
              ) : upcoming.length > 0 ? (
                <>
                  Next up: <span className="font-semibold">{upcoming[0].zoneName}</span> at{' '}
                  <span className="font-semibold tabular-nums">
                    {formatStartClock(upcoming[0].startsAtMs, orgTz)}
                  </span>{' '}
                  <span className="tabular-nums text-gray-500">
                    (in {formatRunTime(upcoming[0].startsInSec)})
                  </span>
                </>
              ) : hasSchedule ? (
                'No further runs scheduled today.'
              ) : (
                'Schedule state not reported — timings unavailable.'
              )}
            </p>
          </div>
        </div>

        {/* Conditions + health at a glance. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700">
          {toNum(conditions.wx?.temp_c) != null && (
            <span className="flex items-center gap-1.5">
              <Thermometer className="h-4 w-4 text-orange-500" />
              {formatNum(conditions.wx?.temp_c, 1)}°C
            </span>
          )}
          {toNum(conditions.wx?.wind_kph) != null && (
            <span className="flex items-center gap-1.5">
              <Wind className="h-4 w-4 text-gray-500" />
              {formatNum(conditions.wx?.wind_kph, 0)} km/h
            </span>
          )}
          {toNumOr(conditions.wx?.rain_mm_day) > 0 && (
            <span className="flex items-center gap-1.5">
              <CloudRain className="h-4 w-4 text-blue-500" />
              {formatNum(conditions.wx?.rain_mm_day, 1)} mm today
            </span>
          )}
          {conditions.lowestTank != null && (
            <span className="flex items-center gap-1.5">
              <Droplets
                className={`h-4 w-4 ${
                  conditions.lowestTank < 25 ? 'text-red-500' : 'text-cyan-500'
                }`}
              />
              Tank {conditions.lowestTank.toFixed(0)}%
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Wifi
              className={`h-4 w-4 ${
                routersOnline === routersTotal ? 'text-green-500' : 'text-amber-500'
              }`}
            />
            {routersOnline}/{routersTotal} nodes
          </span>
          {secondsSinceUpdate != null && (
            <span className="text-xs text-gray-400 tabular-nums">
              updated {secondsSinceUpdate}s ago
            </span>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}
